/**
 * Types and functions for working with meshes in both 2d and 3d
 * @module
 */
import { aabb3AddPoint } from "./aabb3";
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import type { Face } from "./brep";
import { faceByThreePoints } from "./brep";
import type { Mat4 } from "./mat4";

export type IndexArray = Uint32Array;
export const IndexArray = Uint32Array;

export function indexArrayNew(size: number): IndexArray {
  return new IndexArray(size);
}

export interface Mesh2 {
  verts: Float32Array;
  tris: IndexArray;

  // In case the length of the array is 1, then the same id is used for all vertices in the mesh.
  ids?: Uint32Array;
}

export interface Mesh3 {
  verts: Float32Array;
  tris: IndexArray;
  norms?: Float32Array;
  uvs1?: Float32Array;
  uvs2?: Float32Array;

  // In case the length of the array is 1, then the same id is used for all vertices in the mesh.
  ids?: Uint32Array;
}

export interface QuadMesh3 extends Mesh3 {
  quads: IndexArray;
}

export interface Mesh3Instances {
  mesh: Mesh3;
  matrices: Mat4[];
}

export interface Mesh3AndId {
  readonly mesh: Mesh3;
  readonly id?: string;
}

export interface Mesh3IdAndName extends Mesh3AndId {
  readonly name: string;
  readonly useMeshUv: boolean;
}

export function mesh2New(verts: Float32Array, tris: Uint32Array, ids?: Uint32Array): Mesh2 {
  return { verts, tris, ids };
}

export function mesh2Copy(mesh: Mesh2): Mesh2 {
  return {
    verts: mesh.verts.slice(),
    tris: mesh.tris.slice(),
    ids: mesh.ids?.slice(),
  };
}

export function mesh3New(
  verts: Float32Array,
  tris: IndexArray,
  norms?: Float32Array,
  uvs1?: Float32Array,
  uvs2?: Float32Array,
  ids?: Uint32Array,
): Mesh3 {
  return { verts, tris, norms, uvs1, uvs2, ids };
}

export function mesh3Copy(mesh: Mesh3): Mesh3 {
  return {
    verts: mesh.verts.slice(),
    tris: mesh.tris.slice(),
    norms: mesh.norms?.slice(),
    uvs1: mesh.uvs1?.slice(),
    uvs2: mesh.uvs2?.slice(),
    ids: mesh.ids?.slice(),
  };
}

export function quadMesh3New(
  verts: Float32Array,
  tris: IndexArray,
  quads: IndexArray,
  norms?: Float32Array,
  uvs1?: Float32Array,
  uvs2?: Float32Array,
  ids?: Uint32Array,
): QuadMesh3 {
  return { verts, tris, quads, norms, uvs1, uvs2, ids };
}

export function getFacesFromMesh(mesh: Mesh3): Face[] {
  const faces: Face[] = [];

  const { verts, tris } = mesh;

  for (let i = 0, l = tris.length; i < l; i += 3) {
    const ind0 = mesh.tris[i];
    const ind1 = mesh.tris[i + 1];
    const ind2 = mesh.tris[i + 2];

    const face = faceByThreePoints(
      verts[ind0 * 3],
      verts[ind0 * 3 + 1],
      verts[ind0 * 3 + 2],
      verts[ind1 * 3],
      verts[ind1 * 3 + 1],
      verts[ind1 * 3 + 2],
      verts[ind2 * 3],
      verts[ind2 * 3 + 1],
      verts[ind2 * 3 + 2],
    );

    // If the triangle is degenerate, face will be null.
    if (face !== null) {
      faces.push(face);
    }
  }

  return faces;
}

export function vertexArrayAABB3(vertices: Float32Array | ArrayLike<number>): AABB3 | null {
  const l = vertices.length;
  if (l < 3) {
    return null;
  }
  const aabb = aabb3New(vertices[0], vertices[1], vertices[2], vertices[0], vertices[1], vertices[2]);
  for (let i = 3; i + 2 < l; i += 3) {
    aabb3AddPoint(aabb, vertices[i], vertices[i + 1], vertices[i + 2]);
  }
  return aabb;
}
